import React, { useState } from 'react'
import { 
  BellIcon, 
  PlusIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  CheckIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'
import {
  useGetNotificationsQuery,
  useGetNotificationStatsQuery,
  useUpdateNotificationStatusMutation,
  useDeleteNotificationMutation
} from '../store/api/notificationsApi'
import { formatDateTimeEST } from '../utils/dateUtils'

const Notifications: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [typeFilter, setTypeFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const page = 1

  // Fetch notifications from API
  const { data: notificationsResponse, isLoading, error, refetch } = useGetNotificationsQuery({
    page,
    limit: 50,
    search: searchTerm || undefined,
    type: typeFilter !== 'all' ? typeFilter : undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    sortBy: 'created_at',
    sortOrder: 'DESC'
  })

  // Fetch notification stats
  const { data: stats } = useGetNotificationStatsQuery()

  // Mutations
  const [updateStatus] = useUpdateNotificationStatusMutation()
  const [deleteNotification] = useDeleteNotificationMutation()

  const notifications = notificationsResponse?.data || []
  const filteredNotifications = notifications.filter(notification => {
    const matchesSearch = notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         notification.message.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesType = typeFilter === 'all' || notification.type === typeFilter
    const matchesStatus = statusFilter === 'all' || notification.status === statusFilter
    return matchesSearch && matchesType && matchesStatus
  })

  const handleSendNow = async (id: number) => {
    try {
      await updateStatus({ id, status: 'SENT' }).unwrap()
      refetch()
    } catch (error: any) {
      alert('Error al enviar notificación: ' + (error?.data?.message || error?.message))
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('¿Estás seguro de que quieres eliminar esta notificación?')) {
      return
    }
    try {
      await deleteNotification(id).unwrap()
      refetch()
    } catch (error: any) {
      alert('Error al eliminar notificación: ' + (error?.data?.message || error?.message))
    }
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'INFO': return 'bg-blue-100 text-blue-800'
      case 'SUCCESS': return 'bg-green-100 text-green-800'
      case 'WARNING': return 'bg-yellow-100 text-yellow-800'
      case 'ERROR': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'SENT': return 'bg-green-100 text-green-800'
      case 'PENDING': return 'bg-yellow-100 text-yellow-800'
      case 'FAILED': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'INFO': return <InformationCircleIcon className="h-4 w-4" />
      case 'SUCCESS': return <CheckCircleIcon className="h-4 w-4" />
      case 'WARNING': return <ExclamationTriangleIcon className="h-4 w-4" />
      case 'ERROR': return <XMarkIcon className="h-4 w-4" />
      default: return <BellIcon className="h-4 w-4" />
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
          <p className="text-gray-600">Manage system notifications and user communications</p>
        </div>
        <button className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2">
          <PlusIcon className="h-5 w-5" />
          Create Notification
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <BellIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">Total Notifications</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.total || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">Sent</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats?.sent || 0}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">Pending</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats?.pending || 0}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                <span className="text-xs text-white font-bold">%</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">Avg. Read Rate</p>
              <p className="text-2xl font-bold text-gray-900">
                {Math.round(stats?.avgReadRate || 0)}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search notifications..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <FunnelIcon className="h-5 w-5 text-gray-400" />
            <select
              className="border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="all">All Types</option>
              <option value="INFO">Info</option>
              <option value="SUCCESS">Success</option>
              <option value="WARNING">Warning</option>
              <option value="ERROR">Error</option>
            </select>
            <select
              className="border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="SENT">Sent</option>
              <option value="PENDING">Pending</option>
              <option value="FAILED">Failed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        {isLoading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Cargando notificaciones...</p>
          </div>
        ) : error ? (
          <div className="p-6 text-center text-red-600">
            <p>Error al cargar notificaciones. Por favor, intenta de nuevo.</p>
            <button 
              onClick={() => refetch()} 
              className="mt-2 text-blue-600 hover:text-blue-800"
            >
              Reintentar
            </button>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <p>No hay notificaciones que coincidan con los filtros.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredNotifications.map((notification) => (
            <div key={notification.id} className="p-6 hover:bg-gray-50">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4 flex-1">
                  <div className={`flex-shrink-0 p-2 rounded-full ${getTypeColor(notification.type)}`}>
                    {getTypeIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-medium text-gray-900">{notification.title}</h3>
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(notification.type)}`}>
                        {notification.type}
                      </span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(notification.status)}`}>
                        {notification.status}
                      </span>
                    </div>
                    <p className="text-gray-700 mb-3">{notification.message}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>Recipients: {notification.recipient}</span>
                      <span>•</span>
                      <span>Created: {formatDateTimeEST(notification.created_at)}</span>
                      {notification.sent_at && (
                        <>
                          <span>•</span>
                          <span>Sent: {formatDateTimeEST(notification.sent_at)}</span>
                        </>
                      )}
                      {notification.status === 'SENT' && (
                        <>
                          <span>•</span>
                          <span>Read: {notification.read_count}/{notification.total_recipients} ({Math.round(notification.read_count / notification.total_recipients * 100)}%)</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                    View Details
                  </button>
                  {notification.status === 'PENDING' && (
                    <button 
                      onClick={() => handleSendNow(notification.id)}
                      className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                    >
                      Send Now
                    </button>
                  )}
                  <button 
                    onClick={() => handleDelete(notification.id)}
                    className="text-red-600 hover:text-red-800 text-sm font-medium"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Broadcast</h3>
          <p className="text-sm text-gray-600 mb-4">Send a quick notification to all users</p>
          <button className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700">
            Create Broadcast
          </button>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4">System Alert</h3>
          <p className="text-sm text-gray-600 mb-4">Send urgent system-wide alerts</p>
          <button className="w-full bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700">
            Create Alert
          </button>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Newsletter</h3>
          <p className="text-sm text-gray-600 mb-4">Create and schedule newsletters</p>
          <button className="w-full bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700">
            Create Newsletter
          </button>
        </div>
      </div>
    </div>
  )
}

export default Notifications 